import React, { useMemo, useEffect } from 'react';
import {
  Row,
  Select,
} from 'antd';
import PropTypes from 'prop-types';

import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';

function UpdateAddTimestampCheckbox({
  onChange,
  initialChecked,
  value,
  sections = [],
  onTimestampConfigChange,
  showInitialsCheckbox = false,
  timestampConfig = { source: 'stepExecution', includeTime: false, includeInitials: false },
}) {
  const {
    source = 'stepExecution',
    includeTime = false,
    includeInitials = false,
    tableFieldId,
    tableColumnKey,
  } = timestampConfig;

  const tableFields = useMemo(() => {
    const fields = [];
    sections.forEach((section) => {
      const { fields: sectionFields = [] } = section;
      sectionFields.forEach((field) => {
        const {
          id: fieldId,
          selectedType,
          configProps: { title: fieldTitle, dataType } = {},
        } = field;

        if (selectedType === 'table' && ['TimeEntry', 'Materials', 'Equipment'].includes(dataType)) {
          fields.push({
            id: fieldId,
            title: fieldTitle,
            dataType,
          });
        }
      });
    });
    return fields;
  }, [sections]);

  const selectedTableField = useMemo(() => {
    if (!tableFieldId) return null;
    return sections
      .flatMap((section) => section.fields || [])
      .find((field) => field.id === tableFieldId);
  }, [tableFieldId, sections]);

  const tableColumns = useMemo(() => {
    if (!selectedTableField || selectedTableField.selectedType !== 'table') return [];

    const { configProps: { columns = [] } = {} } = selectedTableField;
    return columns.filter((col) => {
      const key = col.key.toLowerCase();
      // Only include columns that have date component, exclude time-only columns
      return key.includes('date') && !key.includes('time');
    });
  }, [selectedTableField]);

  const hasTimeInformation = useMemo(() => {
    switch (source) {
      case 'stepExecution':
      case 'formCreated': {
        return true;
      }
      case 'table': {
        if (!tableColumnKey || !selectedTableField || selectedTableField.selectedType !== 'table') return false;
        const { configProps: { dataType } = {} } = selectedTableField;
        const hasTime = (() => {
          switch (dataType) {
            case 'Materials':
            case 'Equipment':
              return tableColumnKey === 'updatedDate';
            default:
              return tableColumnKey.includes('time') || tableColumnKey.includes('Time');
          }
        })();
        return hasTime;
      }
      default:
        return false;
    }
  }, [source, tableColumnKey, selectedTableField]);

  // Automatically uncheck includeTime when hasTimeInformation becomes false
  useEffect(() => {
    if (includeTime && !hasTimeInformation && onTimestampConfigChange) {
      onTimestampConfigChange({
        ...timestampConfig,
        includeTime: false,
      });
    }
  }, [hasTimeInformation, includeTime, timestampConfig, onTimestampConfigChange]);

  const handleSourceChange = (newSource) => {
    const newConfig = {
      ...timestampConfig,
      source: newSource,
      ...(newSource !== 'table' && {
        tableFieldId: undefined,
        tableColumnKey: undefined,
      }),
    };
    onTimestampConfigChange?.(newConfig);
  };

  const handleTableFieldChange = (fieldId) => {
    const newConfig = {
      ...timestampConfig,
      tableFieldId: fieldId,
      tableColumnKey: undefined,
    };
    onTimestampConfigChange?.(newConfig);
  };

  const handleTableColumnChange = (columnKey) => {
    const newConfig = {
      ...timestampConfig,
      tableColumnKey: columnKey,
    };
    onTimestampConfigChange?.(newConfig);
  };

  const handleIncludeTimeChange = (checked) => {
    const newConfig = {
      ...timestampConfig,
      includeTime: checked,
    };
    onTimestampConfigChange?.(newConfig);
  };
  const handleIncludeInitialsChange = (checked) => {
    const newConfig = {
      ...timestampConfig,
      includeInitials: checked,
    };
    onTimestampConfigChange?.(newConfig);
  };

  return (
    <div>
      <Row style={{ marginTop: 5 }}>
        <OnTraccrCheckbox
          onChange={onChange}
          defaultChecked={initialChecked}
          value={value}
          label="Add date/time stamp"
          hoverNode={(
            <div style={{ width: 300 }}>
              If turned on, new text will include date and time stamps
            </div>
          )}
        />
      </Row>

      {value && (
        <>
          {showInitialsCheckbox && (
          <Row style={{ marginTop: 10 }}>
            <OnTraccrCheckbox
              onChange={handleIncludeInitialsChange}
              value={includeInitials}
              label="Include initials in stamp"
              hoverNode={(
                <div style={{ width: 300 }}>
                  If checked, adds form author's initials (e.g., "JW") before the date/time
                  <br />
                  Format: JW May 15, 2025 at 2:41 PM
                </div>
              )}
            />
          </Row>
          )}
          <Row style={{ marginTop: 10 }}>
            Source of Date Stamp
          </Row>
          <Row style={{ margin: '10px 0px' }}>
            <Select
              value={source}
              onChange={handleSourceChange}
              options={[
                { value: 'stepExecution', label: 'Step Execution Date' },
                { value: 'formCreated', label: 'Form Created Date' },
                ...(tableFields.length > 0 ? [{ value: 'table', label: 'Specific field from a form table' }] : []),
              ]}
            />
          </Row>

          {source === 'table' && (
            <>
              <Row style={{ marginTop: 10 }}>
                Select Table
              </Row>
              <Row style={{ margin: '10px 0px' }}>
                <Select
                  value={tableFieldId}
                  onChange={handleTableFieldChange}
                  showSearch
                  optionFilterProp="label"
                  placeholder="Choose a table"
                  options={tableFields.map((field) => ({
                    value: field.id,
                    label: `${field.title} (${field.dataType})`,
                  }))}
                />
              </Row>

              {tableFieldId && tableColumns.length > 0 && (
                <>
                  <Row style={{ marginTop: 10 }}>
                    Select Column
                  </Row>
                  <Row style={{ margin: '10px 0px' }}>
                    <Select
                      value={tableColumnKey}
                      onChange={handleTableColumnChange}
                      showSearch
                      optionFilterProp="label"
                      placeholder="Choose a date column"
                      options={tableColumns.map((column) => ({
                        value: column.key,
                        label: column.name,
                      }))}
                    />
                  </Row>
                </>
              )}
            </>
          )}

          {hasTimeInformation && (
            <Row style={{ marginTop: 10 }}>
              <OnTraccrCheckbox
                onChange={handleIncludeTimeChange}
                value={includeTime}
                label="Include time in date stamp"
                hoverNode={(
                  <div style={{ width: 300 }}>
                    If checked, stamp shows full date + time (May 15, 2025 at 2:41 PM)
                    <br />
                    If unchecked, stamp shows date only (May 15, 2025)
                  </div>
                )}
              />
            </Row>
          )}
        </>
      )}
    </div>
  );
}

UpdateAddTimestampCheckbox.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialChecked: PropTypes.bool,
  value: PropTypes.bool.isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({
    fields: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      selectedType: PropTypes.string,
      configProps: PropTypes.shape({
        title: PropTypes.string,
        dataType: PropTypes.string,
        columns: PropTypes.arrayOf(PropTypes.shape({
          key: PropTypes.string,
          name: PropTypes.string,
        })),
      }),
    })),
  })),
  onTimestampConfigChange: PropTypes.func,
  timestampConfig: PropTypes.shape({
    source: PropTypes.oneOf(['stepExecution', 'formCreated', 'table']),
    includeTime: PropTypes.bool,
    includeInitials: PropTypes.bool,
    tableFieldId: PropTypes.string,
    tableColumnKey: PropTypes.string,
  }),
};

UpdateAddTimestampCheckbox.defaultProps = {
  initialChecked: false,
  sections: [],
  onTimestampConfigChange: null,
  timestampConfig: {
    source: 'stepExecution',
    includeTime: false,
    includeInitials: false,
  },
};

export default UpdateAddTimestampCheckbox;
